/* Max Customs – email signup popup (sections/mc-email-popup.liquid) */
(() => {
  if (customElements.get('mc-email-popup')) return;

  const DAY_MS = 864e5;
  const SESSION_START = 'mc_email_popup_start';
  const SESSION_SHOWN = 'mc_email_popup_shown';
  const SESSION_PENDING = 'mc_email_popup_pending';
  const SESSION_EDITOR = 'mc_email_popup_editor_open';

  const safe = (fn, fallback = null) => {
    try { return fn(); } catch (e) { return fallback; }
  };
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class McEmailPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog.mc-popup');
      if (!this.dialog) return;

      this.key = this.dataset.storageKey || 'mc_email_popup_v1';
      this.delay = Math.max(0, parseInt(this.dataset.delay, 10) || 10) * 1000;
      this.days = parseInt(this.dataset.days, 10);
      if (Number.isNaN(this.days)) this.days = 7;
      this.designMode = this.dataset.designMode === 'true';

      // One popup only: drop any stale copy left behind by a section re-render,
      // then move the dialog to <body> so footer styles can't clip or hide it.
      document.querySelectorAll('body > dialog.mc-popup').forEach((d) => {
        if (d !== this.dialog) d.remove();
      });
      document.body.appendChild(this.dialog);

      this.bind();

      if (this.designMode) {
        this.setupEditor();
        return;
      }
      this.handleReturnFromNativeSubmit();
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.editorHandlers) this.editorHandlers.forEach(([evt, fn]) => document.removeEventListener(evt, fn));
      if (this.dialog) {
        if (this.dialog.open) this.dialog.close();
        this.dialog.remove();
      }
      this.unlockScroll();
    }

    /* ---------- setup ---------- */
    bind() {
      const d = this.dialog;
      this.form = d.querySelector('form');
      this.input = d.querySelector('input[type="email"]');
      this.status = d.querySelector('[data-mc-popup-status]');
      this.submitBtn = d.querySelector('[data-mc-popup-submit]');

      d.addEventListener('pointerdown', (e) => { this.downOnBackdrop = e.target === d; });
      d.addEventListener('click', (e) => {
        if (e.target === d && this.downOnBackdrop) return this.close('dismissed');
        if (e.target.closest('[data-mc-popup-close]')) return this.close('dismissed');
        if (e.target.closest('[data-mc-popup-copy]')) return this.copyCode(e.target.closest('[data-mc-popup-copy]'));
        return undefined;
      });
      // Escape key
      d.addEventListener('cancel', (e) => { e.preventDefault(); this.close('dismissed'); });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      if (this.input) this.input.addEventListener('input', () => {
        this.input.removeAttribute('aria-invalid');
        if (this.status && this.status.classList.contains('is-error')) this.setStatus('');
      });
    }

    setupEditor() {
      const matches = (e) => e.detail && e.detail.sectionId === this.dataset.sectionId;
      const onSelect = (e) => {
        if (!matches(e)) return;
        safe(() => sessionStorage.setItem(SESSION_EDITOR, '1'));
        this.previewInEditor();
      };
      const onDeselect = (e) => {
        if (!matches(e)) return;
        safe(() => sessionStorage.removeItem(SESSION_EDITOR));
        this.close('editor');
      };
      this.editorHandlers = [['shopify:section:select', onSelect], ['shopify:section:deselect', onDeselect]];
      this.editorHandlers.forEach(([evt, fn]) => document.addEventListener(evt, fn));
      // Re-open after a settings change re-renders the section while it is selected
      if (safe(() => sessionStorage.getItem(SESSION_EDITOR))) this.previewInEditor();
    }

    previewInEditor() {
      if (this.dataset.previewSuccess === 'true') this.showView('success', false);
      else this.showView('form', false);
      this.open();
    }

    /* ---------- storage ---------- */
    read() { return safe(() => JSON.parse(localStorage.getItem(this.key))); }

    write(status) {
      if (this.designMode) return;
      safe(() => localStorage.setItem(this.key, JSON.stringify({ status, ts: Date.now() })));
    }

    isSuppressed() {
      const rec = this.read();
      if (!rec) return false;
      if (rec.status === 'subscribed') return true;
      if (rec.status === 'dismissed') return this.days > 0 && Date.now() - rec.ts < this.days * DAY_MS;
      return false;
    }

    isExcludedPath() {
      const list = (this.dataset.exclude || '').split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      if (!list.length) return false;
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      return list.some((p) => {
        const n = p.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
        return n === '/' ? path === '/' : path === n || path.endsWith(n);
      });
    }

    /* ---------- timing ---------- */
    handleReturnFromNativeSubmit() {
      const params = new URLSearchParams(window.location.search);
      const pending = safe(() => sessionStorage.getItem(SESSION_PENDING));
      if (pending) safe(() => sessionStorage.removeItem(SESSION_PENDING));

      if (params.get('customer_posted') === 'true') {
        // Signed up (via this popup or the footer form) – never nag again.
        this.write('subscribed');
        this.handled = true;
        if (pending) {
          this.cleanUrl(params);
          this.subscribed = true;
          this.showView('success', false);
          this.open();
        }
        return;
      }

      const err = this.status && this.status.querySelector('[data-mc-popup-error]');
      if (pending && err) {
        this.handled = true;
        if (/taken|already/i.test(err.textContent)) {
          // Already on the list – still give them the code
          this.write('subscribed');
          this.subscribed = true;
          this.showView('success', false);
        } else {
          this.status.classList.add('is-error');
          if (this.input) this.input.setAttribute('aria-invalid', 'true');
        }
        this.open();
      }
    }

    cleanUrl(params) {
      params.delete('customer_posted');
      const qs = params.toString();
      const hash = window.location.hash === '#MaxCustomsPopupForm' ? '' : window.location.hash;
      safe(() => window.history.replaceState(window.history.state, '', window.location.pathname + (qs ? `?${qs}` : '') + hash));
    }

    schedule() {
      if (this.handled) return;
      // The clock starts when the visitor first lands on the site (this tab/session)
      let start = parseInt(safe(() => sessionStorage.getItem(SESSION_START)), 10);
      if (!start) {
        start = Date.now();
        safe(() => sessionStorage.setItem(SESSION_START, String(start)));
      }
      if (this.isSuppressed() || this.isExcludedPath()) return;
      if (safe(() => sessionStorage.getItem(SESSION_SHOWN))) return;

      const remaining = Math.max(this.delay - (Date.now() - start), 1500);
      this.timer = setTimeout(() => this.tryOpen(), remaining);
    }

    tryOpen() {
      if (!this.isConnected || this.isSuppressed()) return;
      if (document.hidden) {
        this.onVisibility = () => {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', this.onVisibility);
          this.timer = setTimeout(() => this.tryOpen(), 1200);
        };
        document.addEventListener('visibilitychange', this.onVisibility);
        return;
      }
      // Don't interrupt the cart drawer, quick-add, search, etc.
      const busy = Array.from(document.querySelectorAll('dialog[open]')).some((d) => d !== this.dialog);
      if (busy) {
        this.timer = setTimeout(() => this.tryOpen(), 2500);
        return;
      }
      safe(() => sessionStorage.setItem(SESSION_SHOWN, '1'));
      this.open();
    }

    /* ---------- open / close ---------- */
    open() {
      const d = this.dialog;
      if (!d || d.open) return;
      this.lockScroll();
      try { d.showModal(); } catch (e) { d.setAttribute('open', ''); }
      requestAnimationFrame(() => requestAnimationFrame(() => d.classList.add('is-visible')));
      const target = d.querySelector(`[data-mc-popup-view="${this.view || 'form'}"] .mc-popup__title`);
      if (target) target.focus({ preventScroll: true });
    }

    close(reason) {
      const d = this.dialog;
      if (!d || !d.open || this.closing) return;
      this.closing = true;
      if (reason === 'dismissed' && !this.subscribed) this.write('dismissed');
      d.classList.remove('is-visible');
      const done = () => {
        if (d.open) d.close();
        this.unlockScroll();
        this.closing = false;
      };
      if (reducedMotion()) done();
      else setTimeout(done, 220);
    }

    lockScroll() {
      if (this.scrollLocked) return;
      const root = document.documentElement;
      const scrollbar = window.innerWidth - root.clientWidth;
      this.prevOverflow = root.style.overflow;
      this.prevPadding = root.style.paddingRight;
      root.style.overflow = 'hidden';
      if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
      this.scrollLocked = true;
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      const root = document.documentElement;
      root.style.overflow = this.prevOverflow || '';
      root.style.paddingRight = this.prevPadding || '';
      this.scrollLocked = false;
    }

    showView(name, focus = true) {
      this.view = name;
      this.dialog.querySelectorAll('[data-mc-popup-view]').forEach((v) => {
        v.hidden = v.dataset.mcPopupView !== name;
      });
      if (focus) {
        const t = this.dialog.querySelector(`[data-mc-popup-view="${name}"] .mc-popup__title`);
        if (t) t.focus({ preventScroll: true });
      }
    }

    /* ---------- form ---------- */
    setStatus(msg, isError = false) {
      if (!this.status) return;
      this.status.textContent = msg;
      this.status.classList.toggle('is-error', Boolean(isError && msg));
    }

    setLoading(on) {
      if (!this.submitBtn) return;
      this.submitBtn.classList.toggle('is-loading', on);
      this.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    onSubmit(e) {
      if (this.designMode) {
        e.preventDefault();
        return;
      }
      const email = (this.input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        e.preventDefault();
        this.input.setAttribute('aria-invalid', 'true');
        this.setStatus(this.dataset.msgInvalid || 'Please enter a valid email address.', true);
        this.input.focus();
        return;
      }
      this.input.value = email;
      this.setStatus('');
      this.setLoading(true);
      // Let the browser submit normally so Shopify's spam protection (CAPTCHA) can
      // attach its token. After the reload we reopen straight to the success view.
      safe(() => sessionStorage.setItem(SESSION_PENDING, '1'));
      // If they come back with the browser Back button, clear the spinner
      window.addEventListener('pageshow', (ev) => { if (ev.persisted) this.setLoading(false); }, { once: true });
    }

    onSuccess() {
      this.submitting = false;
      this.subscribed = true;
      this.write('subscribed');
      this.setLoading(false);
      this.showView('success');
      document.dispatchEvent(new CustomEvent('mc:email-popup:subscribed', { bubbles: true }));
    }

    async copyCode(btn) {
      const codeEl = this.dialog.querySelector('[data-mc-popup-code]');
      if (!codeEl) return;
      const code = codeEl.textContent.trim();
      let ok = false;
      if (navigator.clipboard && window.isSecureContext) {
        ok = await navigator.clipboard.writeText(code).then(() => true, () => false);
      }
      if (!ok) {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        this.dialog.appendChild(ta);
        ta.select();
        ok = safe(() => document.execCommand('copy'), false);
        ta.remove();
      }
      if (!ok) return;
      const label = btn.querySelector('[data-mc-popup-copy-label]') || btn;
      if (!btn.dataset.original) btn.dataset.original = label.textContent;
      label.textContent = btn.dataset.labelCopied || 'Copied';
      btn.classList.add('is-copied');
      const live = this.dialog.querySelector('[data-mc-popup-live]');
      if (live) live.textContent = `${code} copied to clipboard`;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        label.textContent = btn.dataset.original;
        btn.classList.remove('is-copied');
      }, 2200);
    }
  }

  customElements.define('mc-email-popup', McEmailPopup);
})();
